import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Feather, ArrowLeft, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";

// Back goes to the previous in-app page, or home when this page was opened
// directly — from a link, or by a reviewer pasting the URL — where nav(-1)
// would do nothing or leave the site. React Router records the history index.
const goBack = (nav) => (window.history.state?.idx > 0 ? nav(-1) : nav("/"));

/**
 * Plain-language help for plans, AI words and billing.
 *
 * The copy here is deliberately concrete — real numbers, no marketing hedging.
 * If pricing changes, the numbers in FAQ_SECTIONS must change with it; they are
 * written out rather than pulled from /api/billing/plans so the page still
 * reads correctly for a signed-out visitor.
 */
const FAQ_SECTIONS = [
    {
        heading: "Plans and pricing",
        items: [
            {
                q: "What do the plans cost?",
                a: `Seer is free. Enchanter is $19 a month, or $144 a year — that works out at $12 a month, saving you 37%. Magnus is $39 a month, or $288 a year ($24 a month, 38% off). Aza is $99 a month, or $711 a year ($59 a month, 40% off).`,
            },
            {
                q: "What's the difference between the plans?",
                a: `Mostly how many AI words you get each month, and which model writes them. Seer gives you 10,000 AI words to try, once. Enchanter gives you 50,000 every month. Magnus gives you 120,000 and unlocks our most capable model for drafting. Aza gives you 250,000 and priority when the servers are busy. Everything else — unlimited novels, export, track changes — comes with any paid plan.`,
            },
            {
                q: "Is Seer really free, or is it a trial?",
                a: `It's free and it doesn't expire. You can write and keep up to 3 novels of up to 40,000 words each for as long as you like, and export them whenever you want. The 10,000 AI words are a one-time allowance rather than a monthly one — enough to properly try the AI on a chapter and decide whether it's for you. Once they're used, the AI features stay visible but locked; everything else keeps working.`,
            },
            {
                q: "Which plan should I pick?",
                a: `If you draft mostly on your own and want AI for tightening and proofreading, Enchanter is plenty. If you use AI to draft whole scenes, or you write fast, Magnus. Aza is for people writing at volume — several books a year, or long working days. You can move up or down at any time, so start lower than you think you need.`,
            },
        ],
    },
    {
        heading: "AI words",
        items: [
            {
                q: "What counts as an AI word?",
                a: `Only words the AI writes for you. Words you type yourself never count, no matter how many. Proofreading, rewriting a paragraph, generating a scene — the words that come back from the AI are what's counted.`,
            },
            {
                q: "How do I know how many I've used?",
                a: `There's a usage meter in the app that shows your words used, your allowance, and how much is left. It updates as you write.`,
            },
            {
                q: "When do my words reset?",
                a: `On the 1st of each month, for every paid plan. Unused words don't roll over. Seer's 10,000 are one-time and don't reset.`,
            },
            {
                q: "What happens when I use most of my words?",
                a: `Past 40% of your monthly allowance, drafting moves from our most capable model to our standard one, and past 90% to our fast model, for the rest of the cycle. The difference is modest — it's still good prose — but we'd rather tell you than have you wonder. Quick jobs like proofreading always use the fast model. Your usage meter shows when this happens. It resets with your allowance on the 1st, and buying extra words moves you back immediately.`,
            },
            {
                q: "Why does the model change instead of just stopping?",
                a: `Because stopping mid-chapter is worse. The most capable model costs several times more to run, so a plan that used it without limit would have to cost considerably more. Downshifting lets us keep the price where it is and keep you writing.`,
            },
            {
                q: "What if I run out completely?",
                a: `You can add 50,000 words for $9, straight away, as many times as you need. There's no obligation to upgrade your plan — though if you're buying top-ups most months, the next plan up is usually cheaper.`,
            },
        ],
    },
    {
        heading: "Billing",
        items: [
            {
                q: "How do I pay?",
                a: `By card on the web, through Stripe. If you subscribe in the mobile app, it goes through the app store instead. Either way you get the same account and the same plan on both.`,
            },
            {
                q: "Monthly or yearly?",
                a: `Yearly saves between 37% and 40% depending on the plan, paid up front. Monthly costs more over a year but you can stop at any time. If you're not sure the app will stick for you, start monthly.`,
            },
            {
                q: "Can I switch plans?",
                a: `Yes, any time, in either direction. Your new allowance applies from the switch.`,
            },
            {
                q: "What happens if I cancel?",
                a: `You keep full access until the end of the period you've paid for. After that your account stays open and your manuscripts stay readable and exportable — we never lock you out of your own writing. The AI features and the unlimited-novel allowance stop until you subscribe again.`,
            },
            {
                q: "Do I lose my work if I stop paying?",
                a: `No. Your manuscripts remain yours, readable and exportable to .docx, .epub or .pdf, on every plan including the free one. This is not something we'll change.`,
            },
            {
                q: "Can I get a refund?",
                a: `Get in touch and we'll sort it out. We'd rather refund someone who isn't getting value than keep the money.`,
            },
        ],
    },
    {
        heading: "Your writing",
        items: [
            {
                q: "Is my writing used to train AI models?",
                a: `No. Your manuscripts are yours. We send what's needed to generate the text you asked for, and that's the end of it.`,
            },
            {
                q: "Who owns what the AI writes?",
                a: `You do. Whatever comes out of Day Dreamer is yours to publish, sell or throw away.`,
            },
            {
                q: "Can I export my work?",
                a: `Always, on every plan, to .docx, .epub or .pdf. No paywall on getting your own writing out.`,
            },
        ],
    },
];

function FaqItem({ q, a, testId }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-border/60 last:border-0">
            <button
                type="button"
                data-testid={testId}
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="w-full flex items-start justify-between gap-4 py-4 text-left hover:opacity-80 transition-opacity"
            >
                <span className="text-[15px] font-medium text-foreground">{q}</span>
                <ChevronDown
                    className={`w-4 h-4 mt-1 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <p className="pb-5 pr-8 text-sm leading-relaxed text-muted-foreground">{a}</p>
            )}
        </div>
    );
}

export default function Faq() {
    const nav = useNavigate();
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="px-6 lg:px-12 pt-8 pb-2 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 hover:opacity-80" data-testid="faq-home">
                    <Feather className="w-5 h-5 text-primary" />
                    <span className="font-heading text-2xl tracking-tight">Day Dreamer</span>
                </Link>
                <Button variant="ghost" size="sm" onClick={() => goBack(nav)} data-testid="faq-back">
                    <ArrowLeft className="w-4 h-4" /> {t("common.back")}
                </Button>
            </header>

            <section className="px-6 lg:px-12 pt-10 pb-8 max-w-3xl mx-auto">
                <div className="ui-label mb-4">Help</div>
                <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-[1.1]">
                    Plans, AI words and <em className="text-primary">billing</em>.
                </h1>
                <p className="mt-4 text-muted-foreground">
                    Everything about what you get and what it costs. If your question isn't here,
                    write to us and we'll answer it — and add it to this page.
                </p>
            </section>

            <section className="px-6 lg:px-12 pb-20 max-w-3xl mx-auto space-y-12">
                {FAQ_SECTIONS.map((section) => (
                    <div key={section.heading} data-testid={`faq-section-${section.heading.toLowerCase().replace(/\s+/g, "-")}`}>
                        <h2 className="font-heading text-2xl font-normal mb-2">{section.heading}</h2>
                        <div className="border-t border-border/60">
                            {section.items.map((item, i) => (
                                <FaqItem
                                    key={item.q}
                                    q={item.q}
                                    a={item.a}
                                    testId={`faq-q-${section.heading.toLowerCase().replace(/\s+/g, "-")}-${i}`}
                                />
                            ))}
                        </div>
                    </div>
                ))}

                <div className="rounded-lg border border-border/60 p-6 text-center">
                    <p className="text-sm text-muted-foreground">
                        Still stuck, or want to talk through which plan fits?
                    </p>
                    <Button
                        variant="secondary"
                        className="mt-4 rounded-full"
                        onClick={() => nav("/pricing")}
                        data-testid="faq-to-pricing"
                    >
                        Compare the plans
                    </Button>
                </div>
            </section>
        </div>
    );
}
